import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getVehicleTrackingReportList,
  getAssignedStages,
} from 'src/redux/Actions/VehicleTracking';
import 'src/style/vehicleTrackingList.css';
import Drawer from '@mui/material/Drawer';
import { ReactComponent as ExcelIcon } from 'src/Images/icons/common/excel.svg';
import { ReactComponent as Funnel } from 'src/Images/icons/common/funnel.svg';
import { ReactComponent as BulletPoints } from 'src/Images/icons/common/bulletpoints.svg';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as Calendar } from 'src/Images/icons/common/calendar.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import TableRightDrawer from 'src/components/TableRightDrawer';
import TableContent from 'src/components/VehicleTrackingReport/TableContent';
import Table from 'src/components/Table';
import getColorCode from 'src/utils/GetThemeColor';
import { Col, Row } from 'react-bootstrap';
import { CustomToolTip } from 'src/utils/CustomToolTip';
import FilterMultiSelect from 'src/utils/FilterMultiSelect';
import debounce from 'lodash/debounce';
import Breadcrumb from 'src/components/Breadcrumb';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import usePageTitle from 'src/utils/SetPageTitle';
import { loadStyledExcelExportModules } from 'src/utils/excelExport';
import { useLocation } from 'react-router-dom';

export default function VehicleTrackingListMainContent() {
  usePageTitle(`Vehicle Tracking Report`);
  const location = useLocation();
  const dispatch = useDispatch();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [searchInputChange, setSearchInputChange] = useState('');
  const [searchInput, setSearchInput] = useState(null);
  const searchTexts = [
    'Vehicle Tracking Code',
    'Vehicle Number',
    'Purchase Order Number',
  ];
  const [pageNumber, setPageNumber] = useState(1);

  const [formData, setFormData] = useState({
    startDate: null,
    endDate: null,
    vehicleTrackingCode: [],
    vehicleNumber: [],
    poNumber: [],
    supplierNameSearchTerm: '',
    StageIdList: [],
  });

  const [showFunnel, setShowFunnel] = useState(false);
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;

  const [paginationBatchSize, setPaginationBatchSize] = useState(10);
  const tenantOrganisationId = authReducer?.userData?.organisations?.id;

  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );

  const { vehicleTrackingReportList, assignedStages } =
    vehicleTrackingDataReducer;

  const fieldFilter =
    vehicleTrackingDataReducer?.vehicleTrackingReportList?.fieldMapping;

  const { totalPages } = vehicleTrackingReportList;

  const profileId = authReducer?.userData?.profile_id;

  useEffect(() => {
    dispatch(getAssignedStages(profileId, ['1']));
  }, [profileId]);

  const workflowOptions =
    assignedStages?.map((workflowObj) => ({
      label: workflowObj.name,
      value: workflowObj.id,
    })) || [];

  const handleFilterInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  function handleResetFilter() {
    setFormData({
      startDate: null,
      endDate: null,
      supplierNameSearchTerm: '',
      StageIdList: [],
    });
  }

  function extractIds(selectedItems) {
    return selectedItems?.map((item) => item.value);
  }

  function handleFilterSubmit(searchTermInput) {
    const searchPayload = {
      start_date: formData?.startDate || null,
      end_date: formData?.endDate || null,
      supplier_name_search_term: formData?.supplierNameSearchTerm || null,
      stage_id:
        formData?.StageIdList?.length > 0
          ? extractIds(formData?.StageIdList)
          : null,
      vehicle_tracking_code:
        formData?.vehicleTrackingCode?.length > 0
          ? extractIds(formData?.vehicleTrackingCode)
          : null,
      vehicle_number:
        formData?.vehicleNumber?.length > 0
          ? extractIds(formData?.vehicleNumber)
          : null,
      po_number:
        formData?.poNumber?.length > 0 ? extractIds(formData?.poNumber) : null,
    };

    const params = new URLSearchParams();
    Object.entries(searchPayload).forEach(([key, value]) => {
      Array.isArray(value)
        ? value.forEach((item) => params.append(key, item))
        : value && params.append(key, value);
    });

    const queryString = params.toString();

    dispatch(
      getVehicleTrackingReportList(
        tenantOrganisationId,
        pageNumber,
        searchTermInput,
        paginationBatchSize,
        queryString,
      ),
    );
  }

  const debouncedFilterSubmit = useCallback(
    debounce((searchTermInput) => {
      handleFilterSubmit(searchTermInput);
    }, 400),
    [pageNumber, paginationBatchSize, formData, tenantOrganisationId],
  );
  useEffect(() => {
    debouncedFilterSubmit(searchInput);
    return () => {
      debouncedFilterSubmit.cancel();
    };
  }, [pageNumber, searchInput, paginationBatchSize]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSearchInputChange(
        (prev) =>
          searchTexts[(searchTexts.indexOf(prev) + 1) % searchTexts.length],
      );
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const toggleDrawer = (open) => () => {
    setOpenDrawer(open);
  };

  const VEHICLE_TRACKING_REPORT_PREFERENCE_KEY =
    'savedVehicleTrackingReportColumns';

  const loadVarianceReportPreferences = () => {
    const saved = localStorage.getItem(VEHICLE_TRACKING_REPORT_PREFERENCE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          order: parsed.order || [],
          selected: parsed.selected || ['1', '2', '3', '4', '5', '6', '7'],
        };
      } catch (e) {
        return {
          order: [],
          selected: ['1', '2', '3', '4', '5', '6', '7'],
        };
      }
    }
    return {
      order: [],
      selected: ['1', '2', '3', '4', '5', '6', '7'],
    };
  };

  const { order: initialOrder, selected: initialSelected } =
    loadVarianceReportPreferences();

  const [draggedFieldOrder, setDraggedFieldOrder] = useState(initialOrder);
  const [selectedFieldFilter, setSelectedFieldFilter] =
    useState(initialSelected);
  const filterPageData = {
    1: 20,
    2: 30,
    3: 40,
    4: 50,
    5: 100,
    6: 500,
    7: 1000,
  };

  useEffect(() => {
    localStorage.setItem(
      VEHICLE_TRACKING_REPORT_PREFERENCE_KEY,
      JSON.stringify({
        order: draggedFieldOrder,
        selected: selectedFieldFilter,
      }),
    );
  }, [draggedFieldOrder, selectedFieldFilter]);

  const handleSelectedFieldFilter = (e, value) => {
    let updated = [...selectedFieldFilter];
    if (e.target.checked) {
      updated.push(value);
    } else {
      updated = updated.filter((v) => v !== value);
    }

    setSelectedFieldFilter(updated);
  };

  let filteredFieldFilter = [];

  if (Array.isArray(fieldFilter)) {
    const source =
      draggedFieldOrder?.length > 0 ? draggedFieldOrder : selectedFieldFilter;

    filteredFieldFilter = source
      .map((value) => fieldFilter?.find((field) => field.value === value))
      .filter(Boolean);
  }

  const handleInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
    } else if (e.target.value === '') {
      // Allow clearing the input
      setPageNumber(1);
    }
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const formattedPageNumber = pageNumber < 10 ? `0${pageNumber}` : pageNumber;

  const handleFunnelToggle = () => {
    setShowFunnel(!showFunnel);
  };

  const handleSearchInput = (e) => {
    const { value } = e.target;
    setSearchInput(value);
  };
  const excelSelectChange = (e) => {
    setPaginationBatchSize(parseInt(e.target.value, 10));
    setPageNumber(1); // Reset to first page when changing page size
  };

  async function exportToExcel(headers, vehicleTrackingData) {
    if (!vehicleTrackingData) return;
    const { utils, write, saveAs } = await loadStyledExcelExportModules();

    const headerRow = headers.map((header) => header.label);
    const headerKeys = headers.map((header) => header.key);

    const dataRows = vehicleTrackingData.map((varianceRow) =>
      headerKeys.map((key) => {
        const value = varianceRow[key];
        return Array.isArray(value) ? value.join('\r\n') : value ?? '';
      }),
    );

    const today = new Date();
    const formattedToday = `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`;

    const sheetContent = [
      [authReducer.userData.organisations?.name || 'Organisation Name'],
      ['Weight Bridge Variance Register'],
      [
        `Reporting Period: ${formData.startDate} to ${formData.endDate || formattedToday}`,
      ],
      [],
      headerRow,
      ...dataRows,
    ];

    const worksheet = utils.aoa_to_sheet(sheetContent);
    const totalColumns = headerRow.length;

    // Merge top 3 rows across all columns
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: totalColumns - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: totalColumns - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: totalColumns - 1 } },
    ];

    // Auto column widths (based only on header + data)
    worksheet['!cols'] = headerKeys.map((_, colIndex) => {
      const maxLen = [headerRow, ...dataRows].reduce((max, row) => {
        const cell = row[colIndex];
        const value = typeof cell === 'string' ? cell : String(cell ?? '');
        const lines = value.split('\r\n');
        const longestLine = Math.max(...lines.map((line) => line.length));
        return Math.max(max, longestLine);
      }, 10);
      return { wch: maxLen + 2 };
    });

    // Row height adjustments
    worksheet['!rows'] = sheetContent.map((row) => ({
      hpt:
        20 +
        (row.some((cell) => typeof cell === 'string' && cell.includes('\r\n'))
          ? 20
          : 0),
    }));

    // Apply styling
    const range = utils.decode_range(worksheet['!ref']);
    for (let R = range.s.r; R <= range.e.r; R += 1) {
      for (let C = range.s.c; C <= range.e.c; C += 1) {
        const cellRef = utils.encode_cell({ r: R, c: C });
        const cell = worksheet[cellRef];
        if (cell) {
          const style = {
            alignment: {
              wrapText: true,
              vertical: 'middle',
              horizontal: 'left', // default to left
            },
            font: {
              name: 'Calibri',
              sz: 11,
            },
            border: {
              top: { style: 'thin', color: { rgb: '000000' } },
              bottom: { style: 'thin', color: { rgb: '000000' } },
              left: { style: 'thin', color: { rgb: '000000' } },
              right: { style: 'thin', color: { rgb: '000000' } },
            },
          };

          // Top 3 merged rows: bold, larger, left-aligned
          if (R === 0 || R === 1 || R === 2) {
            style.font.bold = true;
            style.font.sz = 14;
            style.alignment.horizontal = 'left';
          }

          // Header row (R === 4): bold, center, filled
          if (R === 4) {
            style.font.bold = true;
            style.fill = { fgColor: { rgb: 'DCE6F1' } };
            style.alignment.horizontal = 'center';
          }

          // Data rows: center-align
          if (R > 4) {
            style.alignment.horizontal = 'center';
          }

          cell.s = style;
        }
      }
    }

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Vehicle Register Report');

    const excelBuffer = write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    saveAs(
      blob,
      `Vehicle_Register_Report_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  }

  useSetBreadcrumbData(location.pathname, 'Vehicle Tracking Report');

  return (
    <div className="page-layout">
      <div className="div-space-between slider-padding left-padding">
        <div className="div-flex-row cg-5">
          <Breadcrumb />
        </div>
        <Col xxl={2} xl={2} lg={2} className="right-padding">
          <div className="input-wrapper">
            <input
              type="text"
              className="form-input-section input-icon-remove"
              placeholder={`Search by ${searchInputChange}`}
              value={searchInput || ''}
              onChange={handleSearchInput}
            />
            <span className="form-input-section-icon">
              <Search className="icon-size-lg" />
            </span>
          </div>
        </Col>
      </div>
      <div className="left-padding three-col-layout-header div-flex-row div-space-between right-padding div-align-center">
        <div className="text-xxxl text-bold">Vehicle Tracking Report</div>
        <div className="div-flex-row">
          <div className="div-flex-row div-align-center cg-20 right-padding">
            <div className="div-flex-row cg-20 div-align-center">
              <button
                className="icon-btn-none"
                onClick={handleFunnelToggle}
                aria-label="Funnel"
              >
                <Funnel
                  className="icon-size-lg"
                  color={showFunnel ? getColorCode(themeColor) : '#9F9F9F'}
                />
              </button>

              <button
                className="icon-btn-none"
                onClick={toggleDrawer(true)}
                aria-label="Organiser"
              >
                <BulletPoints className="icon-size-lg" color="#9F9F9F" />
              </button>
              <CustomToolTip title="Export Excel" placement="bottom">
                <button
                  className="icon-btn-none"
                  aria-label="Export Excel"
                  onClick={() =>
                    exportToExcel(
                      filteredFieldFilter,
                      vehicleTrackingReportList?.vehicleTrackingList,
                    )
                  }
                >
                  <ExcelIcon className="icon-size-lg" color="#9F9F9F" />
                </button>
              </CustomToolTip>

              <select
                value={paginationBatchSize}
                onChange={excelSelectChange}
                className="filter-input-section"
              >
                <option value={10}>10</option>
                {Object.entries(filterPageData).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value}
                  </option>
                ))}
              </select>
              <Drawer
                anchor="right"
                open={Boolean(openDrawer)}
                onClose={toggleDrawer(false)}
                ModalProps={{
                  keepMounted: false,
                }}
                sx={{ zIndex: 1 }}
              >
                <div className="right-drawer-component-small">
                  <TableRightDrawer
                    toggleDrawer={toggleDrawer}
                    fieldFilter={fieldFilter}
                    selectedFieldFilter={selectedFieldFilter}
                    handleSelectedFieldFilter={handleSelectedFieldFilter}
                    setDraggedFieldOrder={setDraggedFieldOrder}
                    draggedFieldOrder={draggedFieldOrder}
                  />
                </div>
              </Drawer>
            </div>
          </div>
        </div>
      </div>

      <Row className="g-0">
        <Col
          xxl={showFunnel ? 10 : 12}
          xl={showFunnel ? 9 : 12}
          lg={showFunnel ? 8 : 12}
          md={showFunnel ? 8 : 12}
          sm={showFunnel ? 0 : 12}
          xs={showFunnel ? 0 : 12}
          className="border-bottom"
        >
          <div className="form-line-item-container top-padding">
            <div
              className={`table-fixed-container theme-horizontal-scrollable-container-${themeColor}`}
            >
              <Table
                headers={filteredFieldFilter}
                minCellWidth={150}
                tableContent={
                  <TableContent
                    data={vehicleTrackingReportList?.vehicleTrackingList}
                    fieldFilter={filteredFieldFilter}
                    selectedFieldFilter={selectedFieldFilter}
                    draggedFieldOrder={draggedFieldOrder}
                  />
                }
              />
            </div>
          </div>
          <div className="footer-container">
            <div className="div-space-between div-align-center footer-content-for-page">
              <div className="div-flex-row cg-20 pagination-container div-align-center">
                <div className="div-flex-row cg-10 div-align-center">
                  <button
                    className="icon-btn-none"
                    aria-label="Previous page"
                    onClick={handlePreviousPage}
                  >
                    <ArrowLeft
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </button>
                  <input
                    className="page-number-container input-icon-remove text-sm"
                    value={formattedPageNumber}
                    onChange={handleInputChange}
                    type="number"
                    max={totalPages}
                  />
                  <button
                    className="icon-btn-none"
                    aria-label="Next Page"
                    onClick={handleNextPage}
                  >
                    <ArrowRight
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </button>
                </div>
                <span className="text-grey text-sm">
                  Page {pageNumber} of {totalPages}
                </span>
              </div>
            </div>
          </div>
        </Col>
        {showFunnel && (
          <Col
            xxl={showFunnel ? 2 : 0}
            xl={showFunnel ? 3 : 0}
            lg={showFunnel ? 4 : 0}
            md={showFunnel ? 4 : 0}
            sm={showFunnel ? 12 : 0}
            xs={showFunnel ? 12 : 0}
            className="border-left border-bottom"
          >
            <div className="filter-list-item border-bottom">
              <span className="text-sm text-semi-bold">Filter</span>
            </div>
            <div className="div-flex-column div-space-between rg-20">
              <div className="filter-list-item-container-scroll div-flex-column">
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">
                    Select another workflow stage
                  </span>
                  <div className="input-wrapper">
                    <FilterMultiSelect
                      isMulti
                      name="StageIdList"
                      className="multi-select-input-section"
                      onChange={(selectedOption) =>
                        handleFilterInputChange({
                          target: {
                            name: 'StageIdList',
                            value: selectedOption,
                          },
                        })
                      }
                      value={formData?.StageIdList || ''}
                      options={workflowOptions}
                      classNamePrefix="react-select"
                      placeholder="Search WorkFlow Name"
                    />
                    <span className="form-input-section-icon">
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                        style={{ transform: 'rotate(90deg)' }}
                      />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">
                    Select Supplier Name
                  </span>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      name="supplierNameSearchTerm"
                      className="form-input-section input-icon-remove"
                      placeholder="Search by supplier name"
                      value={formData?.supplierNameSearchTerm || ''}
                      onChange={handleFilterInputChange}
                    />
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">Start Date</span>
                  <div className="input-wrapper">
                    <input
                      name="startDate"
                      className="form-input-section input-icon-date"
                      size="small"
                      type="date"
                      value={formData?.startDate || ''}
                      onChange={handleFilterInputChange}
                    />
                    <span className="form-input-section-icon">
                      <Calendar className="icon-size-sm" />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column">
                  <div className="div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">End Date</span>
                    <div className="input-wrapper">
                      <input
                        name="endDate"
                        className="form-input-section input-icon-date"
                        size="small"
                        type="date"
                        value={formData?.endDate || ''}
                        onChange={handleFilterInputChange}
                      />
                      <span className="form-input-section-icon">
                        <Calendar className="icon-size-sm" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                <button
                  className={`common-btn-outlined border-${themeColor}`}
                  onClick={handleResetFilter}
                >
                  Reset
                </button>
                <button
                  className={`common-btn theme-background-${themeColor}`}
                  onClick={() => handleFilterSubmit(searchInput)}
                >
                  Apply
                </button>
              </div>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
}
